import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldAlert, Users, MessageCircleWarning, AlertOctagon, EyeOff, Globe,
  HeartCrack, Bot, Loader2, ArrowRight, Sparkles, Send, CheckCircle2, XCircle,
  Smartphone, Wrench, Zap, Crown, Wallet as WalletIcon, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * AI Security Suite — Iter-33 Phase 1.
 *
 * Eight text-based AI safety tools in one hub, unified with the same premium
 * multi-color tile aesthetic used across the site. Each tool has:
 *  - a coloured tab pill (top row)
 *  - an inline form matching the tool's input schema
 *  - a structured result card (risk %, reasons, actions, one-liner)
 *
 * Quota + wallet-recharge flow is handled by the backend — this page just
 * surfaces the returned quota block below every result.
 */

const TOOLS = [
  {
    id: 'mobile_security', color: '#a78bfa', Icon: Smartphone,
    title: 'Mobile Security Checker', tagline: 'Is my phone hacked or infected?',
    desc: 'Pick your symptoms. AI diagnoses malware / stalkerware / adware and gives a safe cleanup checklist.',
    badge: 'HOT',
    inputs: [
      { key: 'os_type', label: 'Phone OS', type: 'select', options: ['android', 'ios'], default: 'android' },
      { key: 'symptoms', label: 'Symptoms you notice', type: 'multiselect', options: [
        'Random ads popping up',
        'Phone overheating when idle',
        'Battery drains within hours',
        'Unknown apps I did not install',
        'Screen taps register wrong',
        'Data usage suddenly high',
        'Google/Apple account signed out',
        'Calls/SMS I did not make',
        'Browser redirects to random sites',
      ] },
      { key: 'apps_installed_recently', label: 'Any apps installed in the last week? (optional)', type: 'text', placeholder: '> app names' },
      { key: 'battery_drain', label: 'Battery drains unusually fast', type: 'bool', default: false },
      { key: 'unknown_charges', label: 'Unknown SMS/app-store charges appeared', type: 'bool', default: false },
      { key: 'used_public_wifi', label: 'Used public wifi / free hotspot recently', type: 'bool', default: false },
      { key: 'root_or_jailbreak', label: 'Phone is rooted / jailbroken', type: 'bool', default: false },
      { key: 'notes', label: 'Anything else? (optional)', type: 'textarea', placeholder: '> describe what feels off' },
    ],
    submit: async (data) => api.toolsMobileSecurity(data),
    renderResult: (r) => <RiskCard percent={r.risk_pct} verdict={r.verdict} verdictKind={verdictKind(r.risk_pct)} why={r.evidence} redFlags={r.likely_causes} actionsTitle="Do these NOW (in order)" actions={r.immediate_steps} bonusTitle="Then prevent recurrence" bonus={r.long_term_steps} oneLiner={r.one_liner} labelPercent="Compromise risk" />,
  },
  {
    id: 'scam_detector', color: '#ff2d92', Icon: ShieldAlert,
    title: 'Scam Detector', tagline: 'Paste any message → risk %',
    desc: 'SMS · Email · DM · unknown links. AI flags manipulation, urgency, and phishing tells.',
    inputs: [
      { key: 'content', label: 'Message content (or describe the screenshot)', type: 'textarea', required: true, placeholder: '> paste the SMS / DM / email here' },
      { key: 'channel', label: 'Where did you receive it?', type: 'select', options: ['sms', 'email', 'dm', 'call', 'whatsapp', 'website', 'unknown'], default: 'unknown' },
    ],
    submit: async (data) => api.toolsScamDetector(data),
    renderResult: (r) => <RiskCard percent={r.risk_pct} verdict={r.verdict} verdictKind={verdictKind(r.risk_pct)} why={r.why} redFlags={r.red_flags} actions={r.actions} oneLiner={r.one_liner} />,
  },
  {
    id: 'fake_profile', color: '#c084fc', Icon: Users,
    title: 'Fake Profile Checker', tagline: 'Detect impersonators & bots',
    desc: 'Paste the username, bio, follower count, or a description. AI grades authenticity.',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'select', options: ['instagram', 'facebook', 'tiktok', 'twitter', 'telegram', 'other'], default: 'instagram' },
      { key: 'profile_desc', label: 'Profile info', type: 'textarea', required: true, placeholder: '> username, bio, follower count, activity, any suspicious signals' },
    ],
    submit: async (data) => api.toolsFakeProfile(data),
    renderResult: (r) => <RiskCard percent={r.fake_pct} verdict={r.verdict} verdictKind={verdictKind(r.fake_pct)} why={r.signals} positives={r.good_signs} actions={r.actions} oneLiner={r.one_liner} labelPercent="Fake likelihood" />,
  },
  {
    id: 'whatsapp_guard', color: '#25d366', Icon: MessageCircleWarning,
    title: 'WhatsApp Guard', tagline: 'Scan chats for scam patterns',
    desc: 'OTP fraud · investment scams · fake jobs · romance scams · malware links.',
    inputs: [
      { key: 'sender', label: 'Sender (name / number / unknown)', type: 'text', placeholder: '> +91-9xxx or "unknown"' },
      { key: 'chat_content', label: 'Chat content', type: 'textarea', required: true, placeholder: '> paste or describe the conversation' },
    ],
    submit: async (data) => api.toolsWhatsAppGuard(data),
    renderResult: (r) => <ChatScanCard percent={r.risk_pct} categories={r.categories} redFlags={r.red_flags} unsafeLinks={r.unsafe_links} actions={r.actions} oneLiner={r.one_liner} />,
  },
  {
    id: 'hacked_check', color: '#ff7a3d', Icon: AlertOctagon,
    title: 'Account Hacked Checker', tagline: 'Is my Instagram / email hacked?',
    desc: 'Pick your symptoms. AI grades likelihood + gives ordered recovery steps.',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'select', options: ['instagram', 'facebook', 'tiktok', 'twitter', 'gmail', 'outlook', 'other'], default: 'instagram' },
      { key: 'when', label: 'When did it start?', type: 'select', options: ['today', 'week', 'month', 'older'], default: 'today' },
      { key: 'still_have_access', label: 'Do you still have access?', type: 'bool', default: true },
      { key: 'symptoms', label: 'Symptoms you noticed', type: 'multiselect',
        options: [
          'Unknown login from another location',
          'Password change I did not do',
          'Email address changed',
          'Posts or stories I did not make',
          'DMs sent to my contacts',
          'Sudden logout',
          'Friends receiving scam DMs from me',
          'Suspicious linked apps',
        ],
      },
    ],
    submit: async (data) => api.toolsHackedCheck(data),
    renderResult: (r) => <RiskCard percent={r.hacked_pct} verdict={r.verdict} verdictKind={verdictKind(r.hacked_pct)} why={r.evidence} actionsTitle="Do these NOW (in order)" actions={r.immediate_steps} bonusTitle="Then prevent it happening again" bonus={r.long_term_steps} oneLiner={r.one_liner} labelPercent="Hacked likelihood" />,
  },
  {
    id: 'privacy_leak', color: '#4de0ff', Icon: EyeOff,
    title: 'Privacy Leak Scanner', tagline: 'How exposed is your identity?',
    desc: 'Get a grade + concrete privacy-hardening steps based on your username / email / phone patterns.',
    inputs: [
      { key: 'email', label: 'Email (optional)', type: 'text', placeholder: '> you@example.com' },
      { key: 'username', label: 'Username (optional)', type: 'text', placeholder: '> your handle' },
      { key: 'phone', label: 'Phone pattern (optional)', type: 'text', placeholder: '> +91 9x-xxxx-1234' },
    ],
    submit: async (data) => api.toolsPrivacyLeak(data),
    renderResult: (r) => <PrivacyCard score={r.exposure_score} grade={r.grade} riskFactors={r.risk_factors} platforms={r.likely_platforms} actions={r.actions} oneLiner={r.one_liner} />,
  },
  {
    id: 'website_scam', color: '#ffd34d', Icon: Globe,
    title: 'Website Scam Checker', tagline: 'Is this shopping site safe?',
    desc: 'Paste the URL. AI checks brand-lookalikes, sketchy TLDs, and phishing patterns.',
    inputs: [
      { key: 'url', label: 'Website URL', type: 'text', required: true, placeholder: '> https://amaz0n-india.shop/...' },
      { key: 'context', label: 'Where did you find it? (optional)', type: 'text', placeholder: '> Instagram ad, SMS, WhatsApp forward' },
    ],
    submit: async (data) => api.toolsWebsiteScam(data),
    renderResult: (r) => <RiskCard percent={100 - Number(r.trust_score || 0)} verdict={r.verdict} verdictKind={verdictKindTrust(r.trust_score)} why={r.domain_warnings} positives={r.positive_signals} actions={r.actions} oneLiner={r.one_liner} labelPercent="Danger score" />,
  },
  {
    id: 'cyberbully', color: '#f472b6', Icon: HeartCrack,
    title: 'Cyberbullying Protector', tagline: 'Detect threats & harassment',
    desc: 'AI reads the chat, flags patterns, drafts a report you can submit + supportive message.',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'select', options: ['instagram', 'facebook', 'tiktok', 'whatsapp', 'discord', 'twitter', 'snapchat', 'other'], default: 'instagram' },
      { key: 'target_is_minor', label: 'Is the target under 18?', type: 'bool', default: false },
      { key: 'chat_content', label: 'Chat content', type: 'textarea', required: true, placeholder: '> paste the harassing messages here' },
    ],
    submit: async (data) => api.toolsCyberbully(data),
    renderResult: (r) => <BullyCard threat={r.threat_level} categories={r.categories} quotes={r.quotes} supportive={r.supportive_message} report={r.report_template} actions={r.safety_actions} urgent={r.urgent} />,
  },
  {
    id: 'safety_assistant', color: '#00ff9d', Icon: Bot,
    title: 'Digital Safety Assistant', tagline: 'Chat: "my phone is acting weird…"',
    desc: 'Conversational guide. Describe your situation in your own words, get step-by-step help.',
    isChat: true,
  },
];

const verdictKind = (pct) => {
  const p = Number(pct || 0);
  if (p >= 80) return 'danger';
  if (p >= 50) return 'warn';
  if (p >= 25) return 'caution';
  return 'safe';
};
const verdictKindTrust = (trustScore) => {
  const p = Number(trustScore || 0);
  if (p <= 20) return 'danger';
  if (p <= 50) return 'warn';
  if (p <= 75) return 'caution';
  return 'safe';
};

const KIND_META = {
  safe:    { color: '#00ff9d', label: 'LOOKS SAFE'    },
  caution: { color: '#4de0ff', label: 'BE CAREFUL'    },
  warn:    { color: '#ffd34d', label: 'SUSPICIOUS'    },
  danger:  { color: '#ff3148', label: 'HIGH RISK'     },
};

// Interlink tool results → paid services. If the result is benign, we still
// nudge a soft upsell (Operative Pass) so no diagnosis ends without a next
// step. Returns null only if the tool intentionally handles its own CTAs.
const serviceLeadFor = (toolId, result) => {
  if (!result) return null;
  const pct = Number(result.risk_pct || result.fake_pct || result.hacked_pct || result.exposure_score || (100 - Number(result.trust_score || 0)) || 0);
  const dangerous = pct >= 50 || ['likely_scam','confirmed_scam','likely_fake','confirmed_fake','likely_hacked','confirmed_hacked','very_likely_scam','likely_compromised','compromised','high','urgent'].includes(result.verdict || result.threat_level);

  const LEADS = {
    scam_detector: dangerous
      ? { title: 'Got hit by this scam?', desc: 'Our recovery specialists can freeze the damage — fake-charge disputes, account lock-down, ID protection.', to: '/recovery', cta: 'GET EXPERT RECOVERY', color: '#ff2d92', Icon: Wrench }
      : { title: 'Level up your defence', desc: 'Operative Pass unlocks unlimited scam checks + priority support.', to: '/subscribe', cta: 'SEE OPERATIVE PASS', color: '#ffd34d', Icon: Crown },
    fake_profile: dangerous
      ? { title: 'Is someone impersonating you?', desc: 'We file takedown reports across Instagram, Facebook, TikTok — with proof packet included.', to: '/recovery', cta: 'REQUEST TAKEDOWN', color: '#c084fc', Icon: Wrench }
      : { title: 'Own your handle across platforms', desc: 'Learn the anti-impersonation playbook and access the takedown template library.', to: '/books', cta: 'OPEN LIBRARY', color: '#4de0ff', Icon: BookOpen },
    whatsapp_guard: dangerous
      ? { title: 'Your account may already be compromised', desc: 'If you shared an OTP or clicked a link, act fast — recovery odds are best in the first 24h.', to: '/recovery', cta: 'START RECOVERY NOW', color: '#25d366', Icon: Wrench }
      : { title: 'Prevent the next attempt', desc: 'Operative Pass gets you priority + free re-checks any time.', to: '/subscribe', cta: 'SEE OPERATIVE PASS', color: '#ffd34d', Icon: Crown },
    hacked_check: dangerous
      ? { title: 'Get your account back — the right way', desc: 'Our specialists handle Instagram, Facebook, Gmail, Snap. Wallet-first, no upfront cost surprises.', to: '/recovery', cta: 'START RECOVERY', color: '#ff7a3d', Icon: Wrench }
      : { title: 'Harden before it happens', desc: 'Read: 5-minute account audit + best practice cheatsheet.', to: '/books', cta: 'SEE OUR EBOOKS', color: '#4de0ff', Icon: Sparkles },
    privacy_leak: { title: 'Wipe your exposed identity', desc: 'Data broker opt-outs, doxxing takedowns, fresh burner emails — done for you.', to: '/recovery', cta: 'PRIVACY CLEANUP', color: '#4de0ff', Icon: Wrench },
    website_scam: dangerous
      ? { title: 'Did you pay this site?', desc: 'Recovery specialists can dispute charges + freeze card + report the domain.', to: '/recovery', cta: 'START DISPUTE', color: '#ffd34d', Icon: Wrench }
      : { title: 'Learn to spot the next one', desc: 'Our anti-scam ebook — 40 real examples, 20 rules.', to: '/books', cta: 'GET THE EBOOK', color: '#4de0ff', Icon: Sparkles },
    cyberbully: { title: 'You deserve real backup', desc: 'Our recovery team can help you report, escalate, and get accounts removed. You are not alone.', to: '/recovery', cta: 'GET HELP NOW', color: '#f472b6', Icon: Wrench },
    mobile_security: dangerous
      ? { title: 'Full mobile cleanup + recovery', desc: 'We walk you through a safe factory reset + banking / social account lockdown from another device.', to: '/recovery', cta: 'BOOK CLEANUP SESSION', color: '#a78bfa', Icon: Wrench }
      : { title: 'Stay one step ahead', desc: 'Operative Pass unlocks unlimited phone health scans.', to: '/subscribe', cta: 'SEE OPERATIVE PASS', color: '#ffd34d', Icon: Crown },
    safety_assistant: { title: 'Need hands-on help?', desc: 'The founder + recovery specialists are one tap away. Every case reviewed within 24h.', to: '/recovery', cta: 'TALK TO A SPECIALIST', color: '#00ff9d', Icon: Wrench },
  };
  return LEADS[toolId] || null;
};

const ServiceLeadCard = ({ toolId, result }) => {
  const lead = serviceLeadFor(toolId, result);
  if (!lead) return null;
  const Icon = lead.Icon || Wrench;
  return (
    <div
      className="mt-5 rounded-xl border-2 p-4 sm:p-5 relative overflow-hidden"
      style={{ borderColor: `${lead.color}55`, background: `linear-gradient(135deg, ${lead.color}10 0%, transparent 65%)` }}
      data-testid="security-lead-card"
    >
      <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${lead.color}, transparent)` }} />
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${lead.color}18`, border: `1px solid ${lead.color}55`, color: lead.color }}>
          <Icon size={20} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-1" style={{ color: lead.color }}>// NEXT STEP</div>
          <div className="font-bold text-sm sm:text-base leading-tight" style={{ fontFamily: 'Inter,sans-serif' }}>{lead.title}</div>
          <div className="text-[12.5px] opacity-80 mt-1 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{lead.desc}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={lead.to}
          className="inline-flex items-center gap-1.5 eh-mono text-[11px] tracking-widest font-bold px-3.5 py-2 rounded-md transition-all hover:brightness-110"
          style={{ background: lead.color, color: '#000' }}
          data-testid="security-lead-cta"
        >
          {lead.cta} <ArrowRight size={12} />
        </Link>
        <Link
          to="/me/wallet"
          className="inline-flex items-center gap-1.5 eh-mono text-[11px] tracking-widest px-3 py-2 rounded-md border transition-colors hover:border-[var(--eh-green)]"
          style={{ borderColor: 'var(--eh-border)' }}
        >
          <WalletIcon size={11} /> TOP UP WALLET
        </Link>
      </div>
    </div>
  );
};

const SecuritySuitePage = () => {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(TOOLS[0].id);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [formState, setFormState] = useState({});
  // For the chat assistant tool
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSessionId] = useState(() => `safety-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const tool = useMemo(() => TOOLS.find(t => t.id === activeId), [activeId]);

  const switchTool = (id) => {
    setActiveId(id);
    setResult(null);
    setQuota(null);
    setFormState({});
    if (id !== 'safety_assistant') setChatInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in first — these tools debit your wallet after the free daily quota.'); return; }
    setBusy(true);
    setResult(null);
    try {
      // Validate required fields
      for (const f of tool.inputs) {
        if (f.required && !String(formState[f.key] || '').trim()) {
          toast.error(`${f.label} is required`);
          setBusy(false);
          return;
        }
      }
      const payload = { ...formState };
      // Coerce booleans + arrays if missing
      tool.inputs.forEach(f => {
        if (f.type === 'bool' && payload[f.key] === undefined) payload[f.key] = f.default;
        if (f.type === 'multiselect' && !payload[f.key]) payload[f.key] = [];
      });
      const out = await tool.submit(payload);
      setResult(out.result);
      setQuota(out.quota);
    } catch (err) {
      handleQuotaError(err, tool.id);
    } finally {
      setBusy(false);
    }
  };

  const handleQuotaError = (err, toolId) => {
    const detail = err.detail;
    if (detail && detail.limit_reached) {
      if (detail.top_up_required) {
        toast.error(`Wallet short. Top up ₹${Math.ceil(detail.needed)} to continue.`, {
          action: { label: 'TOP UP', onClick: () => window.location.assign('/me/wallet') },
        });
      } else if (detail.auth_required) {
        toast.error(detail.message || 'Sign in to keep going');
      } else {
        toast.error(detail.message || 'Daily limit reached');
      }
    } else {
      toast.error(err.message || `Could not run ${toolId}`);
    }
  };

  const sendChat = async () => {
    if (!user) { toast.error('Sign in to chat with the Safety Assistant'); return; }
    const msg = chatInput.trim();
    if (!msg) return;
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setBusy(true);
    try {
      const out = await api.toolsSafetyAssistant({ session_id: chatSessionId, message: msg });
      setChatMessages(prev => [...prev, { role: 'bot', text: out.reply }]);
      setQuota(out.quota);
    } catch (err) {
      handleQuotaError(err, 'safety_assistant');
    } finally {
      setBusy(false);
    }
  };

  const meta = tool ? { color: tool.color, label: tool.title } : null;

  return (
    <section className="relative min-h-screen px-3 sm:px-6 py-8 sm:py-12 max-w-6xl mx-auto" data-testid="security-suite-page">
      {/* Ambient blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-20 -left-12 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: '#ff2d92' }} />
      <div aria-hidden className="pointer-events-none absolute top-40 right-0 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#00ff9d' }} />

      <div className="relative">
        <div className="eh-kicker mb-2">// AI · SECURITY SUITE · 9 TOOLS</div>
        <h1 className="eh-display text-3xl sm:text-5xl font-black leading-tight">
          <span>Stay </span><span className="eh-neon">safe online</span><span>, in 30 seconds.</span>
        </h1>
        <p className="mt-3 text-sm sm:text-base opacity-75 max-w-2xl leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
          Nine AI tools built for regular users — spot scams, unmask fake profiles, check if your account (or phone) was hacked, and get calm, step-by-step help.
        </p>

        {/* Tool tabs */}
        <div className="mt-7 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="security-tabs">
          {TOOLS.map(t => {
            const active = t.id === activeId;
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                onClick={() => switchTool(t.id)}
                data-testid={`security-tab-${t.id}`}
                className="shrink-0 relative flex items-center gap-2 px-3.5 py-2.5 rounded-full border transition-all whitespace-nowrap"
                style={{
                  borderColor: active ? t.color : 'var(--eh-border)',
                  background: active ? `${t.color}18` : 'transparent',
                  color: active ? t.color : 'var(--eh-text)',
                }}
              >
                <Icon size={14} />
                <span className="eh-mono text-[11px] tracking-widest font-bold uppercase">{t.title}</span>
                {t.badge && (
                  <span
                    className="eh-mono text-[8px] tracking-widest font-bold px-1.5 py-0.5 rounded"
                    style={{ background: '#ff2d92', color: '#000' }}
                  >
                    {t.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active tool card */}
        {tool && (
          <div
            className="mt-6 rounded-2xl border-2 p-5 sm:p-7"
            style={{
              borderColor: `${meta.color}55`,
              background: `linear-gradient(160deg, ${meta.color}0c 0%, transparent 65%)`,
            }}
          >
            <span aria-hidden className="block h-[3px] -mt-6 -mx-6 mb-5 rounded-t-2xl" style={{ background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)` }} />
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}55`, color: meta.color }}>
                <tool.Icon size={20} strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="eh-mono text-[10px] tracking-widest opacity-70">// {tool.tagline}</div>
                <div className="font-bold text-base sm:text-lg" style={{ fontFamily: 'Inter,sans-serif' }}>{tool.title}</div>
                <div className="text-xs opacity-70 mt-1 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{tool.desc}</div>
              </div>
            </div>

            {/* ================ CHAT UI (Safety Assistant) ================ */}
            {tool.isChat ? (
              <>
                <ChatPanel
                  messages={chatMessages}
                  input={chatInput}
                  setInput={setChatInput}
                  onSend={sendChat}
                  busy={busy}
                  color={meta.color}
                  loggedIn={!!user}
                />
                {chatMessages.length >= 2 && (
                  <ServiceLeadCard toolId={tool.id} result={{ verdict: 'high' }} />
                )}
              </>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="security-form">
                  {tool.inputs.map(f => (
                    <FieldRenderer key={f.key} field={f} value={formState[f.key]} setValue={(v) => setFormState(s => ({ ...s, [f.key]: v }))} color={meta.color} />
                  ))}

                  {!user ? (
                    <Link
                      to={`/login?next=${encodeURIComponent('/tools/security')}`}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-md font-bold text-sm tracking-wider"
                      style={{ background: meta.color, color: '#000' }}
                      data-testid="security-login-cta"
                    >
                      SIGN IN TO USE — 3 FREE / DAY <ArrowRight size={16} />
                    </Link>
                  ) : (
                    <button
                      type="submit"
                      disabled={busy}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-md font-bold text-sm tracking-wider disabled:opacity-50"
                      style={{ background: meta.color, color: '#000' }}
                      data-testid="security-submit"
                    >
                      {busy ? <><Loader2 size={14} className="animate-spin" /> ANALYSING…</> : <><Sparkles size={14} /> RUN AI ANALYSIS <ArrowRight size={16} /></>}
                    </button>
                  )}
                </form>

                {/* Result card */}
                {result && (
                  <div className="mt-6 pt-6 border-t border-[var(--eh-border)]" data-testid="security-result">
                    {tool.renderResult(result)}
                    <ServiceLeadCard toolId={tool.id} result={result} />
                  </div>
                )}
              </>
            )}

            {/* Quota + upsell footer */}
            {quota && <QuotaFooter quota={quota} color={meta.color} />}
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/tools" className="eh-mono text-[11px] tracking-widest opacity-65 hover:opacity-100 hover:text-[var(--eh-green)]">
            ← ALL AI TOOLS
          </Link>
        </div>
      </div>
    </section>
  );
};

// -------- FIELD RENDERERS --------

const FieldRenderer = ({ field, value, setValue, color }) => {
  const common = { style: { borderColor: 'var(--eh-border)' } };
  if (field.type === 'text') {
    return (
      <div>
        <label className="eh-mono text-[10px] tracking-widest opacity-70 mb-1.5 block">{field.label.toUpperCase()}</label>
        <input value={value || ''} onChange={e => setValue(e.target.value)} placeholder={field.placeholder} className="eh-input w-full text-sm" {...common} data-testid={`sec-input-${field.key}`} />
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div>
        <label className="eh-mono text-[10px] tracking-widest opacity-70 mb-1.5 block">{field.label.toUpperCase()}</label>
        <textarea rows={5} value={value || ''} onChange={e => setValue(e.target.value)} placeholder={field.placeholder} className="eh-input w-full text-sm resize-y" data-testid={`sec-input-${field.key}`} />
      </div>
    );
  }
  if (field.type === 'select') {
    const curr = value ?? field.default ?? field.options[0];
    return (
      <div>
        <label className="eh-mono text-[10px] tracking-widest opacity-70 mb-1.5 block">{field.label.toUpperCase()}</label>
        <select value={curr} onChange={e => setValue(e.target.value)} className="eh-input w-full text-sm" data-testid={`sec-input-${field.key}`}>
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'bool') {
    const curr = value ?? field.default ?? false;
    return (
      <label className="flex items-center gap-2 cursor-pointer" data-testid={`sec-input-${field.key}`}>
        <input type="checkbox" checked={!!curr} onChange={e => setValue(e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" />
        <span className="text-sm" style={{ fontFamily: 'Inter,sans-serif' }}>{field.label}</span>
      </label>
    );
  }
  if (field.type === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      if (arr.includes(o)) setValue(arr.filter(x => x !== o));
      else setValue([...arr, o]);
    };
    return (
      <div>
        <label className="eh-mono text-[10px] tracking-widest opacity-70 mb-1.5 block">{field.label.toUpperCase()}</label>
        <div className="flex flex-wrap gap-2" data-testid={`sec-input-${field.key}`}>
          {field.options.map(o => {
            const on = arr.includes(o);
            return (
              <button
                type="button"
                key={o}
                onClick={() => toggle(o)}
                className="px-3 py-1.5 rounded-md eh-mono text-[11px] tracking-wide border transition-colors"
                style={on ? { borderColor: color, color, background: `${color}14` } : { borderColor: 'var(--eh-border)' }}
              >
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

// -------- RESULT CARDS --------

const RiskGauge = ({ percent, kind, label }) => {
  const p = Math.max(0, Math.min(100, Number(percent || 0)));
  const meta = KIND_META[kind] || KIND_META.warn;
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <circle cx="50" cy="50" r="42" fill="none" stroke="var(--eh-border)" strokeWidth="10" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={meta.color} strokeWidth="10" strokeDasharray={`${(p / 100) * 264} 264`} strokeLinecap="round" transform="rotate(-90 50 50)" />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="font-black text-xl leading-none" style={{ color: meta.color }}>{p}%</div>
            <div className="eh-mono text-[8px] opacity-60 mt-0.5">{label}</div>
          </div>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="eh-mono text-[10px] tracking-widest opacity-60">// VERDICT</div>
        <div className="font-black text-base sm:text-lg" style={{ color: meta.color, fontFamily: 'Inter,sans-serif' }}>{meta.label}</div>
      </div>
    </div>
  );
};

const RiskCard = ({ percent, verdict, verdictKind, why, positives, redFlags, actions, actionsTitle, bonus, bonusTitle, oneLiner, labelPercent = 'Risk' }) => (
  <div className="space-y-5">
    <RiskGauge percent={percent} kind={verdictKind} label={labelPercent} />
    {oneLiner && (
      <div className="text-[13px] sm:text-sm italic opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
        &ldquo;{oneLiner}&rdquo;
      </div>
    )}
    {why && why.length > 0 && (
      <BulletList title="Why we think so" items={why} icon={<XCircle size={13} className="text-[#ff7a3d]" />} />
    )}
    {redFlags && redFlags.length > 0 && (
      <BulletList title="Red flags spotted" items={redFlags} pill color="#ff3148" />
    )}
    {positives && positives.length > 0 && (
      <BulletList title="Positive signals" items={positives} icon={<CheckCircle2 size={13} className="text-[#00ff9d]" />} />
    )}
    {actions && actions.length > 0 && (
      <BulletList title={actionsTitle || 'What to do'} items={actions} numbered />
    )}
    {bonus && bonus.length > 0 && (
      <BulletList title={bonusTitle || 'Then also'} items={bonus} icon={<Sparkles size={13} className="text-[#ffd34d]" />} />
    )}
    {verdict && <div className="eh-mono text-[10px] opacity-50">// raw_verdict: {verdict}</div>}
  </div>
);

const ChatScanCard = ({ percent, categories, redFlags, unsafeLinks, actions, oneLiner }) => (
  <div className="space-y-5">
    <RiskGauge percent={percent} kind={verdictKind(percent)} label="Risk" />
    {oneLiner && <div className="text-[13px] italic opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>&ldquo;{oneLiner}&rdquo;</div>}
    {categories && categories.length > 0 && (
      <div>
        <div className="eh-mono text-[10px] tracking-widest opacity-60 mb-2">// SCAM CATEGORIES</div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <span key={c} className="eh-mono text-[10px] tracking-widest px-2 py-1 rounded" style={{ background: c === 'safe' ? 'rgba(0,255,157,.14)' : 'rgba(255,45,72,.14)', color: c === 'safe' ? '#00ff9d' : '#ff2d92' }}>
              {c.replace(/_/g, ' ').toUpperCase()}
            </span>
          ))}
        </div>
      </div>
    )}
    {unsafeLinks && unsafeLinks.length > 0 && <BulletList title="Unsafe links" items={unsafeLinks} pill color="#ff3148" />}
    {redFlags && redFlags.length > 0 && <BulletList title="Manipulation patterns" items={redFlags} icon={<XCircle size={13} className="text-[#ff7a3d]" />} />}
    {actions && actions.length > 0 && <BulletList title="Do this now" items={actions} numbered />}
  </div>
);

const PrivacyCard = ({ score, grade, riskFactors, platforms, actions, oneLiner }) => {
  const gradeMeta = {
    A: { color: '#00ff9d', text: 'excellent' },
    B: { color: '#4de0ff', text: 'good' },
    C: { color: '#ffd34d', text: 'okay' },
    D: { color: '#ff7a3d', text: 'exposed' },
    F: { color: '#ff3148', text: 'fully exposed' },
  }[String(grade || 'C').toUpperCase()] || { color: '#ffd34d', text: 'okay' };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-2xl grid place-items-center shrink-0 border-2" style={{ borderColor: gradeMeta.color, background: `${gradeMeta.color}12` }}>
          <div className="text-center">
            <div className="font-black text-4xl leading-none" style={{ color: gradeMeta.color, fontFamily: "'Cinzel', serif" }}>{String(grade || 'C').toUpperCase()}</div>
            <div className="eh-mono text-[9px] opacity-60 mt-1">{score}/100</div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="eh-mono text-[10px] tracking-widest opacity-60">// PRIVACY GRADE</div>
          <div className="font-bold text-base capitalize" style={{ color: gradeMeta.color, fontFamily: 'Inter,sans-serif' }}>{gradeMeta.text}</div>
        </div>
      </div>
      {oneLiner && <div className="text-[13px] italic opacity-90" style={{ fontFamily: 'Inter,sans-serif' }}>&ldquo;{oneLiner}&rdquo;</div>}
      {riskFactors && riskFactors.length > 0 && <BulletList title="Risk factors" items={riskFactors} icon={<XCircle size={13} className="text-[#ff7a3d]" />} />}
      {platforms && platforms.length > 0 && <BulletList title="Likely platforms this appears on" items={platforms} pill color="#4de0ff" />}
      {actions && actions.length > 0 && <BulletList title="Harden your privacy" items={actions} numbered />}
    </div>
  );
};

const BullyCard = ({ threat, categories, quotes, supportive, report, actions, urgent }) => {
  const threatMeta = {
    none: { color: '#00ff9d', label: 'NO THREAT' },
    low: { color: '#4de0ff', label: 'LOW' },
    medium: { color: '#ffd34d', label: 'MEDIUM' },
    high: { color: '#ff7a3d', label: 'HIGH' },
    urgent: { color: '#ff3148', label: 'URGENT' },
  }[String(threat || 'medium').toLowerCase()] || { color: '#ffd34d', label: 'MEDIUM' };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Report copied to clipboard'); };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-2xl grid place-items-center shrink-0 border-2" style={{ borderColor: threatMeta.color, background: `${threatMeta.color}12` }}>
          <HeartCrack size={30} style={{ color: threatMeta.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eh-mono text-[10px] tracking-widest opacity-60">// THREAT LEVEL</div>
          <div className="font-black text-lg" style={{ color: threatMeta.color, fontFamily: 'Inter,sans-serif' }}>{threatMeta.label}</div>
          {urgent && <div className="text-[11px] mt-1" style={{ color: '#ff3148', fontFamily: 'Inter,sans-serif' }}>⚠ Contact your local emergency helpline immediately.</div>}
        </div>
      </div>
      {supportive && (
        <div className="rounded-md border p-4 leading-relaxed" style={{ borderColor: '#00ff9d55', background: 'rgba(0,255,157,.05)' }}>
          <div className="eh-mono text-[10px] tracking-widest text-[#00ff9d] mb-1.5">// YOU ARE NOT ALONE</div>
          <div className="text-[13px]" style={{ fontFamily: 'Inter,sans-serif' }}>{supportive}</div>
        </div>
      )}
      {categories && categories.length > 0 && <BulletList title="Behaviours detected" items={categories.map(c => c.replace(/_/g, ' '))} pill color="#ff2d92" />}
      {quotes && quotes.length > 0 && <BulletList title="Flagged quotes" items={quotes} icon={<XCircle size={13} className="text-[#ff7a3d]" />} />}
      {actions && actions.length > 0 && <BulletList title="Safety actions" items={actions} numbered />}
      {report && (
        <div className="rounded-md border border-[var(--eh-border)] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="eh-mono text-[10px] tracking-widest opacity-60">// PLATFORM REPORT · PASTE-READY</div>
            <button onClick={() => copy(report)} className="eh-mono text-[10px] tracking-widest px-2 py-1 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] hover:text-[var(--eh-green)] transition-colors">COPY</button>
          </div>
          <pre className="text-[12px] whitespace-pre-wrap opacity-90" style={{ fontFamily: 'Inter,sans-serif' }}>{report}</pre>
        </div>
      )}
    </div>
  );
};

const BulletList = ({ title, items, icon, numbered, pill, color }) => (
  <div>
    <div className="eh-mono text-[10px] tracking-widest opacity-60 mb-2">// {title.toUpperCase()}</div>
    {pill ? (
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={i} className="eh-mono text-[10px] tracking-widest px-2 py-1 rounded break-all" style={{ background: `${color}18`, color }}>{it}</span>
        ))}
      </div>
    ) : (
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] leading-snug" style={{ fontFamily: 'Inter,sans-serif' }}>
            {numbered
              ? <span className="eh-mono text-[10px] opacity-60 shrink-0 pt-1">{i + 1}.</span>
              : (icon || <span className="w-1.5 h-1.5 rounded-full bg-[var(--eh-green)] mt-2 shrink-0" />)}
            <span>{it}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// -------- CHAT PANEL (Safety Assistant) --------

const ChatPanel = ({ messages, input, setInput, onSend, busy, color, loggedIn }) => (
  <div>
    <div className="rounded-md border border-[var(--eh-border)] p-3 min-h-[220px] max-h-[420px] overflow-y-auto space-y-3 bg-black/20" data-testid="safety-chat-log">
      {messages.length === 0 ? (
        <div className="text-center py-8 opacity-60">
          <Bot size={24} className="mx-auto mb-2" style={{ color }} />
          <div className="text-[13px]" style={{ fontFamily: 'Inter,sans-serif' }}>
            Try saying <i>&ldquo;my phone is acting weird&rdquo;</i> or <i>&ldquo;I got a scam WhatsApp forward&rdquo;</i>.
          </div>
        </div>
      ) : (
        messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className="max-w-[85%] rounded-md px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap"
              style={{
                background: m.role === 'user' ? `${color}18` : 'rgba(255,255,255,.05)',
                border: `1px solid ${m.role === 'user' ? color + '55' : 'var(--eh-border)'}`,
                fontFamily: 'Inter,sans-serif',
              }}
              data-testid={`safety-msg-${i}`}
            >
              {m.text}
            </div>
          </div>
        ))
      )}
      {busy && (
        <div className="flex justify-start">
          <div className="rounded-md px-3 py-2 text-[13px] opacity-70" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid var(--eh-border)' }}>
            <Loader2 size={14} className="inline animate-spin mr-2" /> thinking…
          </div>
        </div>
      )}
    </div>
    <div className="mt-3 flex gap-2">
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
        placeholder={loggedIn ? '> describe what happened…' : '> sign in to chat'}
        disabled={!loggedIn || busy}
        className="eh-input flex-1 text-sm"
        data-testid="safety-chat-input"
      />
      <button
        onClick={onSend}
        disabled={!loggedIn || busy || !input.trim()}
        className="px-4 rounded-md font-bold text-sm disabled:opacity-40 flex items-center gap-2"
        style={{ background: color, color: '#000' }}
        data-testid="safety-chat-send"
      >
        <Send size={14} />
      </button>
    </div>
  </div>
);

// -------- QUOTA FOOTER --------

const QuotaFooter = ({ quota, color }) => {
  if (!quota) return null;
  if (quota.source === 'admin') return null;
  return (
    <div className="mt-5 pt-4 border-t border-[var(--eh-border)] flex items-center justify-between text-[11px] eh-mono" data-testid="security-quota">
      <div className="opacity-70">
        // used <b style={{ color }}>{quota.used}</b>/<b>{quota.free_limit}</b> free today
        {quota.source === 'wallet' && <> · paid: <b style={{ color: '#ffd34d' }}>₹{quota.cost}</b> · balance <b>₹{quota.balance_after}</b></>}
      </div>
      {quota.remaining_free === 0 && (
        <Link to="/me/wallet" className="opacity-70 hover:opacity-100" style={{ color }}>
          TOP UP →
        </Link>
      )}
    </div>
  );
};

export default SecuritySuitePage;
